import { Footer } from '@/components/layout/Footer';
import { AnnouncementBanner } from '@/components/ui/AnnouncementBanner';
import { BalanceBar } from '@/components/ui/BalanceBar';
import { ActiveDealWidget } from '@/components/P2P/ActiveDealWidget';
import { UserInfoBlock } from '@/components/ui/UserInfoBlock';
import { TopSellers } from '@/components/Home/TopSellers';
import { UserStatsGauge } from '@/components/Home/UserStatsGauge';
import { UserGrowthCard } from '@/components/Home/UserGrowthCard';
import { useState, useEffect, useMemo } from 'react';
import { useHapticFeedback } from '@/hooks/useHapticFeedback';
import { useStore } from '@/store/useStore';
import { homeApi, cardApi, userApi, p2pApi, adminApi } from '@/utils/api';
import { getCached, setCached, isCacheFresh, TTL, getAny } from '@/utils/cache';
import { CardDetailsModal } from '@/components/Modals/CardDetailsModal';
import { StatusModal } from '@/components/Modals/StatusModal';
import StakingRulesModal from '@/components/Modals/StakingRulesModal';
import { Globe, Flame, Zap, TrendingUp, Sparkles, Gift, PartyPopper, ShoppingBag, ShoppingBasket, Bell, History, Wallet, Lock, Upload, Shield, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNotify } from '@/context/NotificationContext';
import { useTranslation } from 'react-i18next';
// import PremiumItemPage from './PremiumItemPage'; // Unused

// Mocks Removed

const FILTER_OPTIONS = [
    { id: 'skins', label: 'Skins', icon: <Gift className="w-3 h-3 text-purple-400" /> },
    { id: 'cards', label: 'Cards', icon: <Sparkles className="w-3 h-3 text-blue-400" /> },
    { id: 'common', label: 'Common', icon: <Zap className="w-3 h-3 text-gray-400" /> },
    { id: 'popular', label: 'Popular', icon: <Flame className="w-3 h-3 text-orange-500" /> },
    { id: 'live', label: 'Live', icon: <PartyPopper className="w-3 h-3 text-red-500" /> },
];

interface HomeProps {
    onNavigate: (page: string, category?: any) => void;
    onSearch?: () => void;
    isActive?: boolean;
}

const Home = ({ onNavigate, onSearch, isActive = true }: HomeProps) => {
    const { user } = useStore();
    const isAdmin = Boolean(user?.role === 'admin' || String(user?.telegram_id) === '601287447' || (user as any)?.is_admin);
    const { impactOccurred, notificationOccurred } = useHapticFeedback();
    const { showNotification } = useNotify();
    const { t } = useTranslation('home');
    const { t: tc } = useTranslation('common');
    const [activeFilter, setActiveFilter] = useState('all');
    const [quickFind, setQuickFind] = useState('');
    const [selectedCard, setSelectedCard] = useState<any | null>(null);
    const [sellerFilter, setSellerFilter] = useState<string[]>([]);
    const [typeFilter, setTypeFilter] = useState<string[]>([]);
    const [maxPriceFilter, setMaxPriceFilter] = useState<number | null>(null);
    const [tileBgs, setTileBgs] = useState<Record<string, string>>({});
    const [uploadingKey, setUploadingKey] = useState<string | null>(null);

    useEffect(() => {
        homeApi.getProfileTileBgs()
            .then(r => r.json())
            .then(d => {
                if (d?.success && d.backgrounds) {
                    setTileBgs(d.backgrounds);
                }
            })
            .catch(() => {});
    }, []);

    const handleMediaUpload = async (key: string, file: File) => {
        if (!isAdmin) return;
        setUploadingKey(key);
        impactOccurred('medium');
        try {
            const res = await adminApi.uploadUIAsset(file);
            const data = await res.json();
            if (data.success && data.url) {
                const updatedBgs = { ...tileBgs, [key]: data.url };
                setTileBgs(updatedBgs);
                await adminApi.setProfileTileBgs({ [key]: data.url });
                showNotification(tc('success'), t('mediaUpdated'), 'success');
            } else {
                showNotification(tc('error'), data.error || t('uploadFailed'), 'error');
            }
        } catch (err) {
            console.error('[Upload Media Error]', err);
            showNotification(tc('error'), t('mediaUploadFailed'), 'error');
        } finally {
            setUploadingKey(null);
        }
    };

    // Data State (Initialize from persistent cache immediately to prevent disappearing panels)
    const [sections, setSections] = useState<any[]>(() => getAny<{ sections: any[] }>('home_sections')?.sections || []);
    const [loading, setLoading] = useState(() => !getAny('home_sections'));
    const [activeUsers, setActiveUsers] = useState<any[]>(() => getAny<{ users: any[] }>('active_users')?.users || []);
    const [totalActive, setTotalActive] = useState(() => getAny<{ totalActive: number }>('active_users')?.totalActive || 0);
    const [discovery, setDiscovery] = useState<{ brands: any[], skins: any[], cards: any[] }>(() => 
        getAny<{ discovery: any }>('home_sections')?.discovery || { brands: [], skins: [], cards: [] }
    );
    const [statusModal, setStatusModal] = useState<{ isOpen: boolean; type: 'success' | 'error' | 'loading'; title: string; message?: string; onAction?: () => void; }>({
        isOpen: false,
        type: 'success',
        title: '',
    });

    // Claim State
    const [showClaim, setShowClaim] = useState(false); // The overlay
    const [claimedCards, setClaimedCards] = useState<any[]>([]); // The results
    const [isClaiming, setIsClaiming] = useState(false);
    const [claimStep, setClaimStep] = useState<'initial' | 'revealed'>('initial');
    const [showRulesModal, setShowRulesModal] = useState(false);

    useEffect(() => {
        // @ts-ignore
        const tgUser = window.Telegram?.WebApp?.initDataUnsafe?.user;
        const userId = tgUser?.id;

        // ─── Live Pulse: Always fetch active users on mount ───
        // (Even if sections are fresh, we want real-time user count)
        const fetchActiveUsers = () => {
            userApi.getActive().then(r => r.json()).then(data => {
                if (data.success) {
                    setActiveUsers(data.users || []);
                    setTotalActive(data.totalActive || 0);
                    setCached('active_users', { users: data.users, totalActive: data.totalActive });
                }
            }).catch(console.error);
        };
        fetchActiveUsers();

        // ─── Background Sync ───
        if (tgUser) userApi.sync(tgUser).catch(console.error);

        // ─── Sections Logic (SWR: Instant render from cache, always sync fresh in background) ───
        homeApi.getSections(userId).then(r => r.json())
            .then(data => {
                if (data.success) {
                    setSections(data.sections || []);
                    if (data.discovery) setDiscovery(data.discovery);
                    setCached('home_sections', { sections: data.sections || [], discovery: data.discovery });
                }
            }).catch(err => {
                console.error('Failed to load home sections:', err);
            }).finally(() => {
                setLoading(false);
            });

        // Check new user card claim
        if (tgUser) checkNewUserClaim(tgUser);
    }, []);

    const checkNewUserClaim = (tgUser: any) => {
        cardApi.getUserCards(tgUser.id)
            .then(res => res.json())
            .then(cards => {
                if (Array.isArray(cards) && cards.length === 0) {
                    setShowClaim(true);
                }
            })
            .catch(console.error);
    };

    const handleClaimStarter = async () => {
        setIsClaiming(true);
        impactOccurred('heavy');

        try {
            // @ts-ignore
            const tgUser = window.Telegram?.WebApp?.initDataUnsafe?.user;

            // 1. Sync User to ensure we have Internal ID
            if (!tgUser) {
                showNotification(tc('error'), t('openInTelegramRequired'), 'error');
                return;
            }

            const syncRes = await userApi.sync(tgUser);
            const syncData = await syncRes.json();

            if (!syncData.success) throw new Error("Sync failed");

            // If sync says we already have cards (cardCount > 0), don't claim
            if (syncData.cardCount > 0) {
                showNotification(t('notice'), t('alreadyHaveCards'), 'info');
                setShowClaim(false);
                return;
            }

            // 2. Claim Starter
            const claimRes = await cardApi.claimStarter(syncData.userId);
            const claimData = await claimRes.json();

            if (claimData.success) {
                setClaimedCards(claimData.cards);
                setClaimStep('revealed');
                notificationOccurred('success');
                // Refresh home sections if needed?
            } else {
                showNotification(t('claimFailed'), t('claimFailedWithError', { error: claimData.error }), 'error');
                setShowClaim(false);
            }

        } catch (e) {
            console.error(e);
            showNotification(tc('error'), t('claimError'), 'error');
            setShowClaim(false);
        } finally {
            setIsClaiming(false);
        }
    };

    const handleBuyPaidCard = async (card: any) => {
        // @ts-ignore
        const tgUser = window.Telegram?.WebApp?.initDataUnsafe?.user;
        if (!tgUser) {
            showNotification(tc('error'), t('openInTelegram'), 'error');
            return;
        }

        // if (!confirm(`Buy ${card.name} for ${card.price} GRAM?`)) return;

        setIsClaiming(true); // Re-use loading state or create new one
        impactOccurred('medium');

        try {
            const res = await cardApi.buy({ userId: tgUser.id, cardId: card.id });
            const data = await res.json();

            if (data.success) {
                notificationOccurred('success');
                impactOccurred('heavy');
                setStatusModal({
                    isOpen: true,
                    type: 'success',
                    title: t('purchaseSuccessful'),
                    message: t('purchaseSuccessMessage', { name: card.name, balance: Number(data.newBalance).toFixed(2) }),
                    onAction: () => {
                        setStatusModal(prev => ({ ...prev, isOpen: false }));
                    }
                });
            } else {
                notificationOccurred('error');
                setStatusModal({
                    isOpen: true,
                    type: 'error',
                    title: t('purchaseFailed'),
                    message: data.error || t('unknownError')
                });
            }
        } catch (e: any) {
            console.error(e);
            setStatusModal({
                isOpen: true,
                type: 'error',
                title: tc('error'),
                message: t('connectionFailed')
            });
        } finally {
            setIsClaiming(false);
        }
    };

    const handleP2PRequest = async (card: any) => {
        // @ts-ignore
        const tgUser = window.Telegram?.WebApp?.initDataUnsafe?.user;
        if (!tgUser) return showNotification('Error', "Please open in Telegram", 'error');

        impactOccurred('heavy');
        try {
            const sellerId = card.owner_telegram_id;

            const res = await p2pApi.requestDeal({
                sellerId: sellerId,
                buyerId: tgUser.id,
                cardId: card.id,
                price: card.price
            });
            const data = await res.json();
            if (data.success) {
                notificationOccurred('success');
                onNavigate('p2p_transaction');
                setSelectedCard(null);
            } else {
                showNotification(t('requestFailed'), t('requestFailedWithError', { error: data.error || t('unknown') }), 'error');
            }
        } catch (e) { console.error(e); showNotification(tc('error'), t('connectionError'), 'error'); }
    };

    const isP2PCard = (card: any) => {
        if (!card || !card.price) return false;
        const paidSection = sections.find(s => s.id === 'paid_drops');
        if (paidSection && paidSection.data.find((c: any) => c.id === card.id)) return false;
        return true;
    };

    const handleFilterApply = (sellerIds: string[], opts?: any) => {
        setSellerFilter(Array.isArray(sellerIds) ? sellerIds : []);
        if (opts?.name !== undefined) setQuickFind(opts.name || '');
        if (opts?.maxPrice !== undefined) setMaxPriceFilter(opts.maxPrice || null);
    };

    const handleSellersFilterApply = (ids: string[]) => {
        setSellerFilter(Array.isArray(ids) ? ids : []);
    };

    // Flatten all section items into one list using the SAME id/seller key the filter bar uses
    const allHomeItems = useMemo(() => {
        const out: any[] = [];
        sections.forEach(s => {
            (s.data || []).forEach((it: any) => {
                out.push({ ...it, sectionId: s.id, sectionTitle: s.title });
            });
        });
        return out;
    }, [sections]);

    const ownerKey = (it: any) =>
        String(it.seller_username || it.owner_username || it.owner?.username || it.seller?.username || it.username || 'Unknown');

    // Apply combined filters (seller/type/price/search) to the flat item pool
    const filteredHomeItems = useMemo(() => {
        return allHomeItems.filter(it => {
            const p = Number(it.price || it.current_bid || it.startPrice || 0);
            if (sellerFilter.length > 0 && !sellerFilter.includes(ownerKey(it))) return false;
            if (typeFilter.length > 0) {
                const t = String(it.type || it.sectionId || 'HOME');
                if (!typeFilter.includes(t)) return false;
            }
            if (maxPriceFilter && p > 0 && p > maxPriceFilter) return false;
            if (quickFind.trim()) {
                const q = quickFind.toLowerCase();
                const name = ownerKey(it).toLowerCase();
                const display = String(it.displayName || it.name || it.seller_name || '').toLowerCase();
                if (!name.includes(q) && !display.includes(q)) return false;
            }
            return true;
        });
    }, [allHomeItems, sellerFilter, typeFilter, maxPriceFilter, quickFind]);

    // Rebuild sections honoring the active filters
    const visibleSections = useMemo(() => {
        if (sellerFilter.length === 0 && typeFilter.length === 0 && !maxPriceFilter && !quickFind.trim()) {
            return sections;
        }
        const filteredIds = new Set(filteredHomeItems.map(it => `${it.sectionId}-${it.id}`));
        return sections
            .map(s => ({
                ...s,
                data: (s.data || []).filter((it: any) => filteredIds.has(`${s.id}-${it.id}`))
            }))
            .filter(s => (s.data || []).length > 0);
    }, [sections, filteredHomeItems, sellerFilter, typeFilter, maxPriceFilter, quickFind]);

    const homeSections = useMemo(() => {
        return visibleSections.filter(s => s.title !== 'Fast Users' && s.title !== 'P2P Users' && s.id !== 'exclusive_packs');
    }, [visibleSections]);

    const soldIds = useMemo(() => {
        const sold = new Set<string>();
        homeSections.forEach(s => {
            if (s.id === 'recently_sold') (s.data || []).forEach((it: any) => sold.add(String(it.id ?? it.auctionId ?? it.auction_id)));
        });
        return sold;
    }, [homeSections]);

    const homeGridItems = useMemo(() => {
        const seen = new Set<string>();
        const out: any[] = [];
        homeSections.forEach(section =>
            (section.data || []).forEach((it: any) => {
                const key = String(it.id ?? it.auctionId ?? it.auction_id);
                if (key && !seen.has(key)) { seen.add(key); out.push({ item: it, section }); }
            })
        );
        return out;
    }, [homeSections]);

    const closeClaim = () => {
        setShowClaim(false);
        // Refresh sections data without full page reload
        // @ts-ignore
        const tgUser = window.Telegram?.WebApp?.initDataUnsafe?.user;
        if (tgUser) {
            setLoading(true);
            homeApi.getSections(tgUser.id)
                .then(r => r.json())
                .then(data => {
                    if (data.success) {
                        setSections(data.sections);
                        setCached('home_sections', { sections: data.sections, discovery: data.discovery });
                    }
                    setLoading(false);
                })
                .catch(() => setLoading(false));
        }
    };

    // Helper to get Icon
    const getIcon = (type: string) => {
        switch (type) {
            case 'trending': return <TrendingUp className="w-5 h-5 text-[#3390EC]" />;
            case 'sparkles': return <Sparkles className="w-5 h-5 text-[#FBBF24]" />;
            case 'flame': return <Flame className="w-5 h-5 text-[#ff4b4b]" />;
            case 'layout': return <Globe className="w-5 h-5 text-blue-400" />;
            case 'box': return <ShoppingBag className="w-5 h-5 text-purple-400" />;
            default: return <Zap className="w-5 h-5 text-yellow-400" />;

        }
    };

    const executeItemAction = (item: any, section: any) => {
        impactOccurred('medium');
        if (section.id === 'recently_sold') {
            if (item.type === 'AUCTION' || item.isAuction) {
                const auctId = String(item.auctionId || item.id || item.auction_id || (item.umktId ? `umkt_${item.umktId}` : ''));
                if (auctId) localStorage.setItem('selected_auction_id', auctId);
                onNavigate('auction_bid', item);
            } else {
                onNavigate('card_view', item);
            }
            return;
        }
        const isAuction = item.isAuction || item.type === 'AUCTION' || section.id === 'trending_auctions';
        const isOffer = item.isOffer || item.type === 'OFFER' || item.type === 'offer' || item.sale_type === 'offers';

        if (isAuction) {
            const auctId = String(item.auctionId || item.id || item.auction_id || (item.umktId ? `umkt_${item.umktId}` : ''));
            if (auctId) localStorage.setItem('selected_auction_id', auctId);
            onNavigate('auction_bid', item);
        } else if (isOffer) {
            onNavigate('offer_view', item);
        } else if (section.id === 'premium_market_drop' || item.type === 'STORE') {
            onNavigate('brand-item', item);
        } else {
            onNavigate('card_view', item);
        }
    };

    return (
        <>
            {/* CLAIM OVERLAY */}
            <AnimatePresence>
                {showClaim && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-tg-bg/80 backdrop-blur-md"
                    >
                        <div className="glass-strong w-full max-w-sm rounded-3xl p-6 flex flex-col items-center text-center">

                            {claimStep === 'initial' ? (
                                <>
                                    <h2 className="text-2xl font-bold text-tg-text mb-2 flex items-center justify-center gap-2">{t('welcomeGift')} <Gift className="text-yellow-400" /></h2>
                                    <p className="text-tg-hint text-sm mb-6">{t('starterPackIntro')}</p>

                                    {/* Blurred Cards Preview */}
                                    <div className="flex justify-center -space-x-4 mb-8">
                                        {[1, 2, 3].map(i => (
                                            <div key={i} className="w-24 h-36 bg-gradient-to-br from-gray-700 to-gray-900 rounded-xl border border-tg-text/5 shadow-xl blur-sm transform hover:-translate-y-2 transition-transform" />
                                        ))}
                                    </div>

                                    <button
                                        onClick={handleClaimStarter}
                                        disabled={isClaiming}
                                        className="w-full py-4 bg-tg-button hover:bg-tg-button/90 text-tg-button-text font-bold rounded-xl transition-all active:scale-95 disabled:opacity-50"
                                    >
                                        {isClaiming ? t('opening') : t('getNow')}
                                    </button>
                                </>
                            ) : (
                                <>
                                    <h2 className="text-2xl font-bold text-tg-text mb-2 flex items-center justify-center gap-2">{t('youGotIt')} <PartyPopper className="text-yellow-400" /></h2>
                                    <p className="text-tg-hint text-sm mb-6">{t('firstCards')}</p>

                                    {/* Revealed Cards */}
                                    <div className="grid grid-cols-3 gap-2 mb-8 w-full">
                                        {claimedCards.map((card, idx) => (
                                            <div key={idx} className="relative aspect-[3/4] rounded-lg overflow-hidden animate-in zoom-in duration-500" style={{ animationDelay: `${idx * 150}ms` }}>
                                                <img
                                                    src={card.image_filename}
                                                    className="w-full h-full object-cover"
                                                />
                                                <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-[10px] p-1 truncate text-white">
                                                    #{card.serial_number}
                                                </div>
                                            </div>
                                        ))}
                                    </div>

                                    <button
                                        onClick={closeClaim}
                                        className="w-full py-4 bg-tg-text/10 hover:bg-tg-text/20 text-tg-text font-bold rounded-xl transition-all active:scale-95"
                                    >
                                        {t('startTrading')}
                                    </button>
                                </>
                            )}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* TELEGRAM GUARD */}
            {!window.Telegram?.WebApp?.initData && (
                <div className="fixed inset-0 z-[200] bg-[#020408] flex flex-col items-center justify-center p-8 text-center">
                    <div className="w-20 h-20 rounded-3xl bg-blue-500/10 flex items-center justify-center mb-6 border border-blue-500/20">
                        <Lock className="w-10 h-10 text-blue-500" />
                    </div>
                    <h1 className="text-2xl font-black mb-2 tracking-tight uppercase italic" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>{t('restrictedAccess')}</h1>
                    <p className="text-gray-500 text-sm mb-8 max-w-xs">{t('telegramGuardMessage')}</p>
                    <a
                        href={`https://t.me/${import.meta.env.VITE_TELEGRAM_BOT_USERNAME}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-8 py-3 bg-blue-500 text-white font-bold rounded-xl shadow-lg shadow-blue-500/20 active:scale-95 transition-all"
                    >
                        {t('launchInTelegram')}
                    </a>
                </div>
            )}

            <div className="pb-0 overflow-x-hidden pt-4">
                <ActiveDealWidget onNavigate={onNavigate} />

                <div className="px-4 mb-2">
                    <BalanceBar
                        onDepositClick={() => onNavigate('wallet', 'deposit')}
                        onRulesClick={() => {
                            impactOccurred('medium');
                            window.dispatchEvent(new CustomEvent('open_settings'));
                        }}
                        logoVariant="lottie"
                        logoActive={isActive}
                    />
                </div>
                <div className="px-4 mb-3">
                    <AnnouncementBanner onNavigate={onNavigate} />
                </div>
                {/* Top Sellers */}
                <TopSellers onNavigate={onNavigate} />

                {/* Live Market + Users — iOS-style gauge widgets (Edge-to-Edge Expanded) */}
                <div className="mt-5 px-4 flex items-stretch justify-between gap-3 w-full">
                    <UserStatsGauge />
                    <UserGrowthCard />
                </div>

                {/* 4 Square Quick Action Cards (Under Stats Gauges) */}
                <div className="mt-4 px-4 mb-4">
                    <div className="grid grid-cols-2 gap-3">
                        {[
                            {
                                key: 'bg_quick_purchases',
                                label: t('purchases'),
                                subLabel: t('purchasesSub'),
                                icon: <ShoppingBasket className="w-[17px] h-[17px] text-blue-300" />,
                                route: 'purchases',
                            },
                            {
                                key: 'bg_quick_notifications',
                                label: t('alerts'),
                                subLabel: t('alertsSub'),
                                icon: <Bell className="w-[17px] h-[17px] text-amber-300" />,
                                route: 'notifications',
                            },
                            {
                                key: 'bg_quick_history',
                                label: tc('history'),
                                subLabel: t('historySub'),
                                icon: <History className="w-[17px] h-[17px] text-purple-300" />,
                                route: 'p2p_history',
                            },
                            {
                                key: 'bg_quick_wallet',
                                label: tc('wallet'),
                                subLabel: t('walletSub'),
                                icon: <Wallet className="w-[17px] h-[17px] text-emerald-300" />,
                                route: 'wallet',
                            },
                        ].map((card) => {
                            const bgUrl = tileBgs[card.key];
                            const isVideo = bgUrl && /\.(mp4|webm|mov)$/i.test(bgUrl);

                            return (
                                <div
                                    key={card.key}
                                    onClick={() => {
                                        impactOccurred('medium');
                                        onNavigate(card.route);
                                    }}
                                    className="relative w-full aspect-square rounded-[22px] glass-strong hover:bg-white/[0.12] active:scale-[0.98] transition-all cursor-pointer p-4 flex flex-col justify-end overflow-hidden border border-white/10 shadow-xl group text-left"
                                >
                                    {/* Custom Background Media (Image or Video) */}
                                    {bgUrl ? (
                                        isVideo ? (
                                            <video
                                                src={bgUrl}
                                                autoPlay
                                                loop
                                                muted
                                                playsInline
                                                className="absolute inset-0 w-full h-full object-cover rounded-[22px] pointer-events-none"
                                            />
                                        ) : (
                                            <img
                                                src={bgUrl}
                                                alt=""
                                                className="absolute inset-0 w-full h-full object-cover rounded-[22px] pointer-events-none"
                                            />
                                        )
                                    ) : (
                                        <div className="glass-sweep rounded-[22px]" />
                                    )}

                                    {/* Darkness gradient overlay for readability */}
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/35 to-black/10 pointer-events-none rounded-[22px]" />

                                    {/* ADMIN ONLY Media Upload Button (Top Corner) */}
                                    {isAdmin && (
                                        <label
                                            onClick={(e) => e.stopPropagation()}
                                            className="absolute top-2.5 right-2.5 z-30 w-7 h-7 rounded-full bg-black/60 border border-white/30 backdrop-blur-md flex items-center justify-center text-white/90 hover:text-white hover:bg-black/90 active:scale-90 transition-all cursor-pointer shadow-lg"
                                            title={t('uploadAdminTitle')}
                                        >
                                            {uploadingKey === card.key ? (
                                                <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                            ) : (
                                                <Upload className="w-3.5 h-3.5" />
                                            )}
                                            <input
                                                type="file"
                                                accept="image/*,video/*,.gif,.mp4,.webm,.mov,.heic,.heif,.jpg,.jpeg,.png"
                                                className="hidden"
                                                onChange={(e) => {
                                                    const file = e.target.files?.[0];
                                                    if (file) handleMediaUpload(card.key, file);
                                                }}
                                            />
                                        </label>
                                    )}

                                    {/* Title with Icon side-by-side & Subtitle */}
                                    <div className="relative z-10 flex flex-col justify-end">
                                        <div className="flex items-center gap-1.5 mb-0.5">
                                            <span className="flex-shrink-0 flex items-center justify-center">
                                                {card.icon}
                                            </span>
                                            <h3 className="text-sm font-black text-white tracking-tight leading-tight group-hover:text-blue-200 transition-colors">
                                                {card.label}
                                            </h3>
                                        </div>
                                        <p className="text-[10px] font-semibold text-white/50 leading-snug line-clamp-1">
                                            {card.subLabel}
                                        </p>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Cards section removed - cards belong in Store/Auction pages only */}

                {/* Modals */}
                <CardDetailsModal
                    isOpen={!!selectedCard}
                    onClose={() => setSelectedCard(null)}
                    card={selectedCard}
                    isP2P={isP2PCard(selectedCard)}
                    onBuy={
                        (selectedCard && 'price' in selectedCard && Number(selectedCard.price) > 0)
                            ? () => {
                                if (isP2PCard(selectedCard)) handleP2PRequest(selectedCard);
                                else handleBuyPaidCard(selectedCard);
                            }
                            : undefined
                    }
                />

                <StatusModal
                    isOpen={statusModal.isOpen}
                    onClose={() => setStatusModal(prev => ({ ...prev, isOpen: false }))}
                    type={statusModal.type}
                    title={statusModal.title}
                    message={statusModal.message}
                    actionLabel={statusModal.type === 'success' ? t('awesome') : tc('close')}
                    onAction={statusModal.onAction}
                />

                <StakingRulesModal
                    isOpen={showRulesModal}
                    onClose={() => setShowRulesModal(false)}
                />

                <Footer onNavigate={onNavigate} />

            </div >
        </>
    );
};

export default Home;
